import { useEffect, useState, type ReactNode } from "react";

import { getCustomAppIcon } from "./customAppIcons";

export interface AppGlyphProps {
  /** Id de l'app / panneau (= clé dans `CUSTOM_APP_ICONS`). */
  slug?: string;
  /** Image d'icône explicite (URL ou data-URL) — apps Convex `external`/`custom`.
   * Prioritaire sur l'icône statique par slug ; fallback `children` si elle échoue. */
  imageUrl?: string;
  /**
   * Classes appliquées à l'`<img>` custom — règle la taille + le rendu.
   * Typiquement `h-X w-X rounded-… object-cover` pour un rendu « tuile ».
   */
  imgClassName: string;
  /** Texte alternatif. Vide (défaut) = décoratif (`aria-hidden`). */
  alt?: string;
  /** Icône Phosphor de repli (rendue si aucune image custom / image KO). */
  children: ReactNode;
}

/**
 * Affiche l'icône custom (image) d'une app si elle existe, sinon l'icône
 * Phosphor passée en `children`. Le repli est aussi déclenché si l'image
 * 404 / échoue à charger (onError). Le mapping slug → fichier vit dans
 * `customAppIcons.ts`.
 */
export function AppGlyph({ slug, imageUrl, imgClassName, alt = "", children }: AppGlyphProps) {
  const src = imageUrl || getCustomAppIcon(slug);
  const [failed, setFailed] = useState(false);
  // Réessaie quand la source change (ex: nouvelle photo uploadée).
  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={alt}
        aria-hidden={alt === "" ? true : undefined}
        draggable={false}
        onError={() => setFailed(true)}
        className={imgClassName}
      />
    );
  }

  return <>{children}</>;
}
